// claudeApi.ts — Shared client for calling Claude Haiku API routes
//
// Handles Firebase auth token + fetch for all four migrated services, avoiding duplication.
// Used by pensumParser, keywordExtractor, prepChat, and annaTextChat
// after migration from direct Gemini REST calls.

import { getAuth } from 'firebase/auth';

/** Get the current user's Firebase ID token for API authentication. */
async function getAuthToken(): Promise<string | null> {
  const user = getAuth().currentUser;
  if (!user) return null;
  return user.getIdToken();
}

/**
 * Call a Claude Haiku API route and return the text response.
 *
 * @param route - API route path (e.g. '/api/parse-pensum')
 * @param body - Request body (route-specific)
 * @returns The text content from Claude's response
 * @throws Error if not authenticated, or if the API route returns an error
 */
export async function callClaudeRoute(
  route: string,
  body: Record<string, unknown>,
): Promise<string> {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Not authenticated — please sign in');
  }

  const response = await fetch(route, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    let errorMsg = `API error: ${response.status}`;
    try {
      const errorBody = await response.json();
      if (errorBody.error) errorMsg = errorBody.error;
    } catch {
      // Use status code message
    }
    throw new Error(errorMsg);
  }

  const data = await response.json();
  return data.content || '';
}
